import React from 'react';
import { IconThumbUp, IconBookmark, IconShare3, IconArrowRight } from '@tabler/icons-react';
import { getDisplayName } from '../../../../../../utils/userDisplay';
import { getSolutionColor } from '../../../../../../utils/solutionUtils';
import AvatarById from '../../../../../../components/common/AvatarById';
import { SolutionPhase } from './SolutionPhase';

export const SolutionCard = ({
  solution,
  currentUser,
  usersEntities,
  highlightedId,
  liking,
  saving,
  sharing,
  expandedPhases,
  setExpandedPhases,
  handleSolutionClick,
  handleProfileClick,
  handleLike,
  handleSave,
  handleShare
}) => {
  if (!solution) return null;

  const solutionColor = getSolutionColor(solution.type);
  const profile = solution?.userId ? usersEntities?.[solution.userId]?.data : null;
  const isPlaceholderProf = (v) => !v || String(v).trim().length === 0 || String(v).toLowerCase() === 'no profession set';
  const chosenAuthorName = getDisplayName({ profile, candidates: [solution?.creatorUsername] });
  const profFromProfile = profile?.userProfession || profile?.profession || null;
  const chosenProfession = isPlaceholderProf(solution?.creatorProfession)
    ? (profFromProfile || null)
    : (solution?.creatorProfession || profFromProfile || null);

  const isHighlighted = highlightedId === solution.id;

  const hasLiked = !!(currentUser?.uid && Array.isArray(solution.likers) && solution.likers.includes(currentUser.uid));
  const isPendingLike = !!liking[solution.id];
  const displayHasLiked = isPendingLike ? !hasLiked : hasLiked;
  const displayedLikes = isPendingLike
    ? (hasLiked ? Math.max(0, (solution.likes || 1) - 1) : (solution.likes || 0) + 1)
    : (solution.likes || 0);

  const hasSaved = !!(currentUser?.uid && Array.isArray(solution.savers) && solution.savers.includes(currentUser.uid));
  const isPendingSave = !!saving[solution.id];
  const displayHasSaved = isPendingSave ? !hasSaved : hasSaved;
  const displayedSaves = isPendingSave
    ? (hasSaved ? Math.max(0, (solution.saves || 1) - 1) : (solution.saves || 0) + 1)
    : (solution.saves || 0);

  const hasShared = !!(currentUser?.uid && Array.isArray(solution.sharers) && solution.sharers.includes(currentUser.uid));
  const isPendingShare = !!sharing[solution.id];
  const displayHasShared = isPendingShare ? !hasShared : hasShared;
  const displayedShares = isPendingShare
    ? (hasShared ? Math.max(0, (solution.shares || 1) - 1) : (solution.shares || 0) + 1)
    : (solution.shares || 0);

  return (
    <div
      id={`solution-${solution.id}`}
      data-solution-id={solution.id}
      data-id={solution.id}
      className={
        `display-solution-card overflow-hidden rounded-lg shadow-sm border-2 transition-all duration-200 ${isHighlighted
          ? 'highlighted border-green-500 bg-green-50'
          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
        }`
      }
      onClick={(e) => handleSolutionClick(solution.id, e)}
    >
      <div className="p-4 border-b border-gray-100" style={{ borderLeft: `4px solid ${solutionColor}` }}>
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-medium text-gray-900">{solution.title && solution.title.trim() ? solution.title : 'Untitled Solution'}</h3>
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <time dateTime={solution.createdAt}>
              {solution.createdAt ? new Date(solution.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }) : ''}
            </time>
          </div>
        </div>
        <div className="mt-1">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: solutionColor }}
          >
            {(() => {
              switch (solution.type) {
                case 'health': return 'Health Solution';
                case 'financial': return 'Financial Solution';
                case 'legal': return 'Legal Solution';
                case 'government': return 'Government Solution';
                default: return 'Solution';
              }
            })()}
          </span>
        </div>

        <div
          className={`mt-2 flex items-center gap-2 ${solution.userId ? 'cursor-pointer hover:text-green-700' : ''}`}
          onClick={(e) => handleProfileClick(solution.userId, e)}
          role={solution.userId ? 'button' : undefined}
          tabIndex={solution.userId ? 0 : -1}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && solution.userId) handleProfileClick(solution.userId, e); }}
        >
          <div className="text-gray-400">
            <AvatarById userId={solution.userId} size="small" />
          </div>
          <div className="text-sm text-gray-600 flex flex-col">
            <span className="font-medium text-gray-900">{chosenAuthorName}</span>
            {chosenProfession && (
              <span className="text-gray-500 text-xs text-left">{chosenProfession}</span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 bg-white">
        {solution.content && solution.content.trim() && (
          <div className="mt-2">
            <div className="prose prose-sm max-w-none text-gray-700">
              <div className="whitespace-pre-wrap">
                {(() => {
                  const halfLength = Math.ceil(solution.content.length / 2);
                  const displayLength = Math.max(halfLength, 300);
                  return solution.content.length > displayLength
                    ? `${solution.content.substring(0, displayLength)}...`
                    : solution.content;
                })()}
              </div>
              {(() => {
                const halfLength = Math.ceil(solution.content.length / 2);
                const displayLength = Math.max(halfLength, 300);
                return solution.content.length > displayLength && (
                  <div className="mt-3 flex items-center justify-center p-2 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors cursor-pointer">
                    <span className="text-sm text-green-700 font-medium mr-2">Continue reading</span>
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {solution.phases && solution.phases.length > 0 && (
          <div className="mt-4">
            <div className="divide-y divide-gray-100">
              {solution.phases.map((phase, phaseIndex) => (
                <SolutionPhase
                  key={`${solution.id}-${phase.id}`}
                  phase={phase}
                  phaseIndex={phaseIndex}
                  solutionId={solution.id}
                  expandedPhases={expandedPhases}
                  setExpandedPhases={setExpandedPhases}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={(e) => { e.stopPropagation(); handleLike(e, solution.id); }}
              aria-pressed={displayHasLiked}
              disabled={!currentUser?.uid || isPendingLike}
            >
              <IconThumbUp size={16} className={displayHasLiked ? 'text-green-600' : 'text-gray-400'} />
              <span>{displayedLikes}</span>
            </button>
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={(e) => { e.stopPropagation(); handleSave(e, solution.id); }}
              aria-pressed={displayHasSaved}
              disabled={!currentUser?.uid || isPendingSave}
            >
              <IconBookmark size={16} className={displayHasSaved ? 'text-green-600' : 'text-gray-400'} />
              <span>{displayedSaves}</span>
            </button>
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={(e) => { e.stopPropagation(); handleShare(e, solution.id); }}
              aria-pressed={displayHasShared}
              disabled={!currentUser?.uid || isPendingShare}
            >
              <IconShare3 size={16} className={displayHasShared ? 'text-green-600' : 'text-gray-400'} />
              <span>{displayedShares}</span>
            </button>
          </div>
          <div className="flex items-center">
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm transition-colors"
              onClick={(e) => handleSolutionClick(solution.id, e)}
            >
              <span>View Full Solution</span>
              <IconArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
